import moment from 'moment'
import differenceInDays from 'date-fns/difference_in_days/index.js'
import fecha from 'fecha'

const pregeneratedZeros = {
  0: '',
  1: '0',
  2: '00',
  3: '000',
}

function pad (value, isFullYear = false) {
  const zeros = isFullYear ? 4 - `${value}`.length : 2 - `${value}`.length
  return pregeneratedZeros[zeros] + value
}

function dateString (date) {
  const hours = `${date.getUTCHours()}`.padStart(2, '0')
  const minutes = `${date.getUTCMinutes()}`.padStart(2, '0')
  const seconds = `${date.getUTCSeconds()}`.padStart(2, '0')
  const milliseconds = `${date.getUTCMilliseconds()}`.padStart(3, '0')

  return `${pad(date.getUTCFullYear(), true)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${hours}:${minutes}:${seconds}.${milliseconds}Z`
}

export default class LocalDate {
  constructor (value) {
    if (!value) {
      const today = new Date()
      this.date = new Date(
        Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate()
        )
      )
    } else if (typeof value === 'string') {
      const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(T\d{2}:\d{2}:\d{2}.\d{3}Z)?/)
      if (!dateMatch) {
        throw new Error(`Only dates in the format "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss.sssZ" are allowed (${value})`)
      }
      this.date = new Date(value)
    } else if (
      typeof value === 'number' ||
      Object.prototype.toString.call(value) === "[object Date]"
    ) {
      this.date = new Date(value)
    } else if (
      typeof value === 'object' &&
      typeof value.toDate === 'function'
    ) {
      this.date = value.toDate()
    } else {
      throw new Error('Please supply a string')
    }
  }

  static fromDate (date) {
    return new LocalDate(dateString(date))
  }

  static parse (string) {
    const match = /^(\d\d)\/(\d\d)\/(\d\d\d\d)/.exec(string)
    if (match) {
      return new LocalDate(match[3] + '-' + match[2] + '-' + match[1])
    }
    throw new Error('Date must be in the format DD/MM/YYYY')
  }

  toJSON () {
    return this.toString()
  }

  toISOString () {
    return this.toDate().toISOString()
  }

  toString () {
    return `${pad(this.getFullYear(), true)}-${pad(this.getMonth() + 1)}-${pad(this.getDate())}`
  }

  toUTCString () {
    return dateString(this.date)
  }

  format (pattern) {
    return fecha.format(new Date(this.getFullYear(), this.getMonth(), this.getDate(), this.getUTCHours(), this.getUTCMinutes(), 0, 0), pattern)
  }

  formatLocal (pattern) {
    return fecha.format(this.date, pattern)
  }

  add (count, quantity) {
    const date = moment.utc(this.date).add(count, quantity).valueOf()
    return new LocalDate(date)
  }

  addHours (count) {
    return this.add(count, 'hours')
  }

  addDays (count) {
    return this.add(count, 'days')
  }

  addMonths (count) {
    return this.add(count, 'months')
  }

  addYears (count) {
    return this.add(count, 'years')
  }

  getDay () {
    return this.date.getUTCDay()
  }

  getDate () {
    return this.date.getUTCDate()
  }

  getMonth () {
    return this.date.getUTCMonth()
  }

  getFullYear () {
    return this.date.getUTCFullYear()
  }

  getUTCFullYear () {
    return this.date.getUTCFullYear()
  }

  getUTCMonth () {
    return this.date.getUTCMonth()
  }

  getUTCDate () {
    return this.date.getUTCDate()
  }

  getUTCHours () {
    return this.date.getUTCHours()
  }

  getUTCMinutes () {
    return this.date.getUTCMinutes()
  }

  getYear () {
    return this.getFullYear().toString().substring(2)
  }

  toDate () {
    return this.date
  }

  valueOf () {
    return this.date.valueOf()
  }

  differenceInDays (date) {
    return differenceInDays(new LocalDate(date).toString(), this.toString())
  }

  isBetween (startDate, endDate) {
    return startDate <= this && this <= endDate
  }

  getTime () {
    return this.date.getTime()
  }

  getTimezoneOffset () {
    return this.date.getTimezoneOffset()
  }
}
